import { PropsWithChildren, useEffect, useState } from 'react';
import { generateCSSBlock, generateCSSVariableBlock } from './helper/css.ts';
import StyleContext, {
	CSSProps,
	CSSVariables,
	ChangeCSSPropsFn,
	ChangeCSSVariableFn,
} from './context.ts';
import { useHydrated } from '../../../../_helpers/hooks/useHydrated.ts';

const Style: React.FC<PropsWithChildren<{ className?: string }>> = ({
	children,
	className,
}) => {
	const [CSSProps, setCSSProps] = useState<CSSProps>({});
	const [CSSVariables, setCSSVariables] = useState<CSSVariables>({});
	const [rootCSSVariables, setRootCSSVariables] = useState<CSSVariables>({});
	const generateRandomID = (): string =>
		'id-' + Math.random().toString(36).slice(2, 17);
	const [randomID, setRandomID] = useState<string>(generateRandomID());

	useEffect(() => {
		setRandomID(generateRandomID());
	}, []);

	const styleContent = Object.keys(CSSProps).map((selector) =>
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		generateCSSBlock('#' + randomID + ' ' + selector, CSSProps[selector])
	);

	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	const variableContent = generateCSSVariableBlock(CSSVariables);
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	const rootVariableContent = generateCSSVariableBlock(rootCSSVariables);

	const changeCSSProps: ChangeCSSPropsFn = (selector, css) => {
		setCSSProps((props) => {
			if (!css) {
				delete props[selector];
				return props;
			}
			return {
				...props,
				[selector]: {
					...props[selector],
					...css,
				},
			};
		});
	};

	const changeCSSVariables: ChangeCSSVariableFn = (
		variableName,
		variableValue
	): void => {
		setCSSVariables(updateCSSVariable(variableValue, variableName));
	};

	const changeRootCSSVariables: ChangeCSSVariableFn = (
		variableName,
		variableValue
	): void => {
		setRootCSSVariables(updateCSSVariable(variableValue, variableName));
	};
	const hasProps = !!Object.keys(CSSProps).length;
	const hasVariables = !!Object.keys(CSSVariables).length;
	const hasRootVariables = !!Object.keys(rootCSSVariables).length;
	const hydrated = useHydrated();
	return (
		<StyleContext.Provider
			value={{
				CSSProps,
				changeCSSProps,
				CSSVariables,
				changeCSSVariables,
				rootCSSVariables,
				changeRootCSSVariables,
			}}
		>
			<style suppressHydrationWarning={true}>
				{hydrated && hasProps && styleContent}
				{hydrated &&
					hasVariables &&
					'#' + randomID + '{' + variableContent + '}'}
				{hydrated && hasRootVariables && ':root {' + rootVariableContent + '}'}
			</style>

			<div id={hydrated ? randomID : ''} className={className}>
				{children}
			</div>
		</StyleContext.Provider>
	);
};

export default Style;

function updateCSSVariable(
	variableValue: string | number,
	variableName: string
) {
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	return (cssVariables) => {
		if (!variableValue) {
			delete cssVariables[variableName];
			return cssVariables;
		}
		return { ...cssVariables, [variableName]: variableValue };
	};
}
